
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { GeneratedBook, BookPage } from '../types';
import { 
  ArrowLeft, ChevronRight, Save, Download, Loader2, 
  Play, Volume2, X, Maximize, Minimize, BookOpen, ChevronLeft, AlertCircle 
} from './Icons';
import { authService } from '../services/authService';
import { storyService, logImageEvent } from '../services/storyService';
import { generateSpeech } from '../services/geminiService';
import { debugLog } from '../utils/debugLogger';

// jsPDF loaded dynamically on first PDF export
const loadJsPDF = (): Promise<any> => {
  if ((window as any).jspdf) return Promise.resolve((window as any).jspdf);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script.onload = () => resolve((window as any).jspdf);
    script.onerror = () => reject(new Error('Failed to load jsPDF'));
    document.head.appendChild(script);
  });
};

interface BookReaderProps {
  book: GeneratedBook;
  onBack: () => void;
}

// Utility to decode base64 audio which is raw PCM data from Gemini TTS
function decode(base64: string) {
  try {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (e) {
    return new Uint8Array(0);
  }
}

let sharedAudioContext: AudioContext | null = null;
function getAudioContext() {
  if (!sharedAudioContext) {
    sharedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
  }
  return sharedAudioContext;
}

/**
 * Component for immersive reading experience with cinematic animations and audio.
 * Fixed to ensure full viewport compatibility on desktop.
 */
const AnimatedReadAloudPlayer: React.FC<{
  page: BookPage;
  bookId?: string;
  voiceName?: string;
  onNext: () => void;
  onExit: () => void;
  isFullScreen: boolean;
  toggleFullScreen: () => void;
}> = ({ page, bookId, voiceName, onNext, onExit, isFullScreen, toggleFullScreen }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);

  // Removed cinematic zoom/pan classes as they were causing unexpected layout shifts and zoom issues
  const animationClass = "animate-fade-in";

  useEffect(() => {
    let isMounted = true;
    const playAudio = async () => {
      let audioSource = page.audioBase64;
      
      setAudioError(null);
      if (!audioSource) {
          setIsGeneratingAudio(true);
          try {
            audioSource = await generateSpeech(page.text, voiceName);
          } catch (e) {
            console.error("On-the-fly TTS generation failed", e);
            debugLog("AUDIO", "On-the-fly TTS generation failed", { page: page.pageNumber });
            setAudioError("Audio unavailable");
          }
          setIsGeneratingAudio(false);
      }

      if (!isMounted) return;

      if (!audioSource || audioSource.length < 10) {
          debugLog("AUDIO", "Missing or short audio source", { page: page.pageNumber });
          setAudioError("Audio unavailable");
          setTimeout(() => isMounted && onNext(), 4000);
          return;
      }

      setIsPlaying(true);
      try {
        const ctx = getAudioContext();
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        
        let audioBytes: Uint8Array;
        
        if (audioSource.startsWith('http')) {
           const controller = new AbortController();
           const timeoutId = setTimeout(() => controller.abort(), 15000);
           try {
             const response = await fetch(audioSource, { signal: controller.signal });
             if (!response.ok) {
               throw new Error(`Audio fetch failed (${response.status})`);
             }
             const contentType = response.headers.get('content-type') || '';
             if (contentType && !contentType.startsWith('audio/') && !contentType.includes('octet-stream')) {
               throw new Error(`Unexpected audio content-type: ${contentType}`);
             }
             const arrayBuffer = await response.arrayBuffer();
             audioBytes = new Uint8Array(arrayBuffer);
           } finally {
             clearTimeout(timeoutId);
           }
        } else {
           audioBytes = decode(audioSource);
        }
        
        if (audioBytes.length === 0) {
           throw new Error("Empty audio bytes after decode");
        }
        if (audioBytes.byteLength % 2 !== 0) {
          throw new Error("Invalid PCM byte length");
        }

        const dataInt16 = new Int16Array(audioBytes.buffer);
        const audioBuffer = ctx.createBuffer(1, dataInt16.length, 24000);
        const channelData = audioBuffer.getChannelData(0);
        for (let i = 0; i < dataInt16.length; i++) {
          channelData[i] = dataInt16[i] / 32768.0;
        }
        
        if (sourceNodeRef.current) {
            try { sourceNodeRef.current.stop(); } catch(e) {}
        }

        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);
        source.onended = () => isMounted && onNext();
        source.start();
        sourceNodeRef.current = source;
      } catch (err) {
        console.error("Playback error:", err);
        debugLog("AUDIO", "Playback error", { page: page.pageNumber });
        setIsPlaying(false);
        setAudioError("Audio error");
        setTimeout(() => isMounted && onNext(), 3000);
      }
    };
    playAudio();
    return () => { 
      isMounted = false; 
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.stop(); } catch(e) {}
      } 
    };
  }, [page, voiceName, onNext]);

  return (
    <div className="fixed inset-0 z-50 bg-[#fdfbf7] paper-texture flex flex-col items-center justify-center overflow-hidden h-[100dvh]">
      <div className={`relative flex flex-col bg-transparent overflow-hidden shadow-2xl transition-all duration-500 box-border ${isFullScreen ? 'w-full h-full' : 'max-w-[1024px] w-[min(100%-32px,1024px)] h-[min(100%-64px,900px)] max-h-[90dvh]'}`}>
          <div className="flex-1 flex flex-col items-center px-6 md:px-16 pt-8 md:pt-12 overflow-y-auto">
              <div className="w-full max-w-3xl text-center mb-6 md:mb-8">
                  <p className="text-[#1a1825] text-xl md:text-3xl font-serif leading-relaxed">
                      {page.text}
                  </p>
              </div>

              <div className="relative flex-1 w-full flex items-center justify-center overflow-hidden rounded-2xl shadow-xl min-h-0 mb-4">
                  {page.imageUrl && (
                    <img
                      src={page.imageUrl}
                      className={`w-full h-full max-h-[50dvh] object-contain ${animationClass}`}
                      alt={`Story illustration for page ${page.pageNumber}`}
                      loading="lazy"
                      decoding="async"
                      onLoad={() => logImageEvent('load_success', page.imageUrl!, bookId, page.pageNumber, { type: 'read-aloud' })}
                      onError={() => logImageEvent('load_error', page.imageUrl!, bookId, page.pageNumber, { type: 'read-aloud' })}
                    />
                  )}
              </div>
          </div>

          <div className="bg-white/60 backdrop-blur-md border-t border-[#1a1825]/5 p-4 md:p-6 flex justify-between items-center no-print mt-auto">
              <button onClick={onExit} className="text-[#1a1825]/40 hover:text-[#1a1825] p-3 transition-colors bg-[#1a1825]/5 rounded-full flex items-center gap-2">
                  <X className="w-5 h-5" />
                  <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-widest">Exit</span>
              </button>

              <div className="flex items-center gap-4">
                  {isGeneratingAudio && <Loader2 className="w-4 h-4 animate-spin text-gold-600" />}
                  <Volume2 className={`w-5 h-5 ${isPlaying ? 'text-gold-600 animate-pulse' : 'text-[#1a1825]/20'}`} />
                  {audioError && (
                    <span className="text-[10px] text-red-500 font-black uppercase tracking-[0.2em] flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {audioError}
                    </span>
                  )}
                  <span className="text-[10px] text-[#1a1825]/40 font-black uppercase tracking-[0.2em]">Page {page.pageNumber}</span>
              </div>

              <button onClick={toggleFullScreen} className="text-[#1a1825]/40 hover:text-[#1a1825] p-3 transition-colors bg-[#1a1825]/5 rounded-full">
                  {isFullScreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
          </div>
      </div>
    </div>
  );
};

/**
 * Main BookReader component.
 * Constrained layout to prevent overflow on desktop/laptop screens.
 */
const BookReader: React.FC<BookReaderProps> = ({ book, onBack }) => {
  const [viewState, setViewState] = useState<'cover' | 'pages'>('cover');
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [isReadAloud, setIsReadAloud] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const lockedVoiceName = useMemo(() => (book.voiceName || 'Kore').trim(), [book.voiceName]);

  const handleStartReadAloud = useCallback(() => {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }
    setIsReadAloud(true);
  }, []);

  const handleNext = useCallback(() => {
    if (viewState === 'cover') {
        setViewState('pages');
        setCurrentPageIdx(0);
        setImageLoaded(false);
        return;
    }
    if (currentPageIdx < book.pages.length - 1) {
      setCurrentPageIdx(prev => prev + 1);
      setImageLoaded(false);
    } else {
      setViewState('cover');
    }
  }, [viewState, currentPageIdx, book.pages.length]);

  const handlePrev = useCallback(() => {
    if (viewState === 'pages') {
        if (currentPageIdx === 0) {
            setViewState('cover');
        } else {
            setCurrentPageIdx(prev => prev - 1);
            setImageLoaded(false);
        }
    }
  }, [viewState, currentPageIdx]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        if (isReadAloud) return;
        if (e.key === 'ArrowRight') handleNext();
        if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, isReadAloud]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus("Saving...");
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error("Please log in to save.");
      const res = await storyService.saveStory(user.id, book, user.isPro);
      if (res.success) {
        setSaveStatus("Saved!");
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus(res.message);
      }
    } catch (e: any) {
      setSaveStatus("Failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (isExportingPDF) return;
    setIsExportingPDF(true);
    setSaveStatus("Preparing PDF...");
    
    try {
      const jspdfModule = await loadJsPDF();
      const { jsPDF } = jspdfModule;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);

      const addPage = async (text: string, imageUrl: string | undefined, isCover: boolean = false, isEnd: boolean = false) => {
        if (!isCover) pdf.addPage();
        
        pdf.setFillColor(253, 251, 247); 
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

        if (isCover) {
          pdf.setTextColor(26, 24, 37);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(36);
          const splitTitle = pdf.splitTextToSize(book.title, contentWidth);
          pdf.text(splitTitle, pageWidth / 2, 40, { align: 'center' });

          if (imageUrl) {
            try {
              const imgProps = pdf.getImageProperties(imageUrl);
              const imgRatio = imgProps.height / imgProps.width;
              const finalHeight = 150;
              const finalWidth = Math.min(contentWidth, finalHeight / imgRatio);
              pdf.addImage(imageUrl, 'JPEG', (pageWidth - finalWidth) / 2, 65, finalWidth, finalHeight);
            } catch (e) {}
          }

          pdf.setFontSize(14);
          pdf.setTextColor(184, 125, 18);
          pdf.text(`By ${book.authorName || 'StoryForge'}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
        
        } else if (isEnd) {
          pdf.setTextColor(222, 160, 30);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(48);
          pdf.text("The End", pageWidth / 2, pageHeight / 2, { align: 'center' });
          
          pdf.setFontSize(10);
          pdf.setTextColor(150, 150, 150);
          pdf.text("Created with StoryForge Kids", pageWidth / 2, pageHeight - 20, { align: 'center' });

        } else {
          pdf.setTextColor(26, 24, 37);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(20);
          const splitText = pdf.splitTextToSize(text, contentWidth);
          pdf.text(splitText, margin, 30);

          const textHeight = splitText.length * 9;
          const imageY = 30 + textHeight + 10;

          if (imageUrl) {
            try {
              const imgProps = pdf.getImageProperties(imageUrl);
              const imgRatio = imgProps.height / imgProps.width;
              const availableHeight = pageHeight - imageY - 25;
              const finalHeight = Math.min(availableHeight, 140);
              const finalWidth = Math.min(contentWidth, finalHeight / imgRatio);
              pdf.addImage(imageUrl, 'JPEG', (pageWidth - finalWidth) / 2, imageY, finalWidth, finalHeight);
            } catch (e) {}
          }

          pdf.setFontSize(10);
          pdf.setTextColor(180, 180, 180);
          pdf.text(`${currentPageIdx + 1}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        }
      };

      await addPage("", book.coverImageUrl, true);
      for (const page of book.pages) {
        await addPage(page.text, page.imageUrl);
      }
      await addPage("", undefined, false, true);

      pdf.save(`${book.title.replace(/\s+/g, '_').toLowerCase()}.pdf`);
      setSaveStatus("PDF Ready!");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      alert("PDF Error. Try again.");
    } finally {
      setIsExportingPDF(false);
    }
  };

  const currentPage = book.pages[currentPageIdx];

  if (isReadAloud && viewState === 'pages') {
    return (
      <AnimatedReadAloudPlayer
        page={currentPage}
        bookId={book.id}
        voiceName={lockedVoiceName}
        onNext={handleNext}
        onExit={() => setIsReadAloud(false)}
        isFullScreen={isFullScreen}
        toggleFullScreen={() => setIsFullScreen(!isFullScreen)}
      />
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#fdfbf7] paper-texture text-[#1a1825] flex flex-col items-center overflow-x-hidden">
      <div className="w-full py-4 px-6 md:px-12 flex justify-between items-center no-print z-50 bg-[#fdfbf7]/90 backdrop-blur-md border-b border-[#1a1825]/5 sticky top-0">
        <button onClick={onBack} className="flex items-center gap-3 text-[#1a1825]/40 hover:text-[#1a1825] transition-all group">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-bold uppercase tracking-widest text-[10px]">Library</span>
        </button>
        
        <div className="flex gap-2 sm:gap-3 items-center">
          <button onClick={handleSave} disabled={isSaving} className="h-10 px-4 rounded-xl bg-[#1a1825]/5 hover:bg-[#1a1825]/10 text-[#1a1825] border border-[#1a1825]/10 flex items-center gap-2 transition-all">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="text-[10px] font-black uppercase tracking-widest">{isSaving ? 'Saving' : (saveStatus || 'Save')}</span>
          </button>
          <button onClick={handleExportPDF} disabled={isExportingPDF} className="h-10 px-4 rounded-xl bg-gradient-to-r from-forge-500 to-gold-500 text-white flex items-center gap-2 transition-all shadow-lg shadow-gold-500/20 disabled:opacity-50">
            {isExportingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="text-[10px] font-black uppercase tracking-widest">Download PDF</span>
          </button>
        </div>
      </div>

      <div className="flex-1 w-full max-w-[1024px] flex items-center justify-center p-4 md:p-8 animate-fade-in relative box-border">
        {viewState === 'cover' && (
            <div className="flex flex-col items-center text-center w-full max-w-4xl space-y-6 md:space-y-10 py-6">
                <div className="space-y-4">
                    <h1 className="text-3xl md:text-6xl font-serif font-bold text-[#1a1825] leading-tight px-4">
                        {book.title}
                    </h1>
                </div>

                <div className="relative group w-full max-w-xs md:max-w-sm">
                    <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl bg-white border border-[#1a1825]/5 relative transition-transform duration-700 hover:scale-[1.02]">
                        {book.coverImageUrl && (
                                        <img
                                            src={book.coverImageUrl}
                                            alt={`Cover of ${book.title || 'story book'}`}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            decoding="async"
                                            onLoad={() => logImageEvent('load_success', book.coverImageUrl!, book.id, -1, { type: 'cover' })}
                                            onError={() => logImageEvent('load_error', book.coverImageUrl!, book.id, -1, { type: 'cover' })}
                                        />
                                    )}
                    </div>
                </div>

                <div className="space-y-6">
                    <p className="text-lg text-gold-600 font-bold tracking-[0.2em] uppercase">By {book.authorName || 'StoryForge'}</p>
                    {book.voiceName && (
                      <p className="text-xs text-gray-500 mt-1">🎙️ Narrated by {book.voiceName}</p>
                    )}
                    <button onClick={handleNext} className="px-12 py-5 bg-[#1a1825] rounded-3xl text-white font-bold text-xl shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-4">
                        Open Book
                        <ChevronRight className="w-6 h-6" />
                    </button>
                </div>
            </div>
        )}

        {viewState === 'pages' && (
            <div className="w-full max-w-4xl flex flex-col items-center h-full">
                <div className="w-full bg-transparent flex flex-col flex-1">
                    <div className="py-6 md:py-8 text-center shrink-0">
                        <p className="text-xl md:text-3xl font-serif text-[#1a1825] leading-relaxed max-w-3xl mx-auto px-4">
                           {currentPage.text}
                        </p>
                    </div>
                    
                    <div className="flex-1 relative flex items-center justify-center min-h-0">
                        {!imageLoaded && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#1a1825]/10" /></div>}
                        {currentPage.imageUrl && (
                            <img
                                src={currentPage.imageUrl}
                                alt={`Story illustration for page ${currentPageIdx + 1}`}
                                className={`w-full h-full max-h-[50dvh] md:max-h-[55dvh] object-contain rounded-2xl shadow-xl transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                                loading="lazy"
                                decoding="async"
                                onLoad={() => {
                                    setImageLoaded(true);
                                    logImageEvent('load_success', currentPage.imageUrl!, book.id, currentPageIdx);
                                }}
                                onError={() => logImageEvent('load_error', currentPage.imageUrl!, book.id, currentPageIdx)}
                            />
                        )}
                        <div className="absolute bottom-4 right-4 no-print">
                             <button onClick={handleStartReadAloud} className="p-4 bg-gold-500 text-white rounded-full hover:scale-110 transition-all shadow-2xl">
                                <Play className="w-5 h-5 fill-current" />
                             </button>
                        </div>
                    </div>
                </div>

                <div className="w-full max-w-xl flex justify-between items-center py-8 px-4 no-print shrink-0">
                    <button onClick={handlePrev} className="w-10 h-10 bg-[#1a1825]/5 rounded-full flex items-center justify-center hover:bg-[#1a1825]/10 transition-all text-[#1a1825]/40">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="text-[10px] text-[#1a1825]/40 font-black uppercase tracking-[0.4em]">Page {currentPageIdx + 1}</div>
                    <button onClick={handleNext} className="w-10 h-10 bg-[#1a1825]/5 rounded-full flex items-center justify-center hover:bg-[#1a1825]/10 transition-all text-[#1a1825]/40">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default BookReader;
